import Link from "@mui/material/Link";
import Skeleton from "@mui/material/Skeleton";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import Guid from "_shared/guids/guid";
import { getIndividualsByUserIds, getIndividualsPendingByUserIds } from "marketplace/individuals/_stores/individuals-redux-slice";
import Individual from "marketplace/individuals/individual";
import { useSelector } from "react-redux";
import * as React from "react";
import TeamTemplate from "work/team-templates/team-template";
import ActiveTeamDisplay from "_shared/teams/active-team-display";
import { MAX_VISIBLE_TEAM_MEMBERS } from "_shared/teams/team-members-display";
import TeamRosterDrawer, { individualProfileDialog } from "_shared/teams/team-roster-drawer";
import { NoValueDash } from "_shared/_views/tables/no-value-dash";
import SearchMatch from "_shared/_views/tables/search-match";
import type { MRT_RowData, MRT_TableInstance } from "material-react-table";

const MembersContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(0.25),
  maxWidth: "100%",
  minWidth: 0,
  overflow: "hidden",
}));

const TeamColumnLink = styled(Link)(({ theme }) => ({
  color: theme.palette.common.black,
  display: "block",
  textDecorationColor: theme.palette.common.black,
  cursor: "pointer",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover": {
    textDecoration: "underline",
  },
}));

const OpenRosterButton = styled("button")(({ theme }) => ({
  alignSelf: "flex-start",
  background: "none",
  border: 0,
  color: theme.palette.primary.main,
  cursor: "pointer",
  font: "inherit",
  padding: 0,
  textDecoration: "underline",
  whiteSpace: "nowrap",
}));

type MembersColumnCellProps<TData extends MRT_RowData> = {
  teamTemplate?: TeamTemplate;
  teamUserIds?: Guid[];
  activeTeam?: ActiveTeamDisplay;
  /** The work the team is on, or the template's name: what the full list is titled. */
  teamName?: string;
  /** The table the cell sits in, whose search the names mark. Only where that search covers the members (#6590). */
  table?: MRT_TableInstance<TData>;
};

/**
 * A team's first few members, each a link to their profile, and the way into the whole team: "+N
 * more" when some do not fit, "View team" when they all do (#6603).
 */
export default function MembersColumnCell<TData extends MRT_RowData>(props: Readonly<MembersColumnCellProps<TData>>) {
  // MRT's table instance is one mutable object, the same prop on every render, so a memoized
  // <SearchMatch table={table} /> would keep the search term it first marked (review of #6801).
  "use no memo";

  const { teamTemplate, teamUserIds, activeTeam, table } = props;
  const { openDialog } = useDialog();
  const [isRosterOpen, setIsRosterOpen] = React.useState(false);

  let membersUserIds: Guid[] = [];
  let leaderUserId: Guid | undefined;

  if (activeTeam) {
    membersUserIds = activeTeam.members?.map((m) => m.userId) || [];
    leaderUserId = activeTeam.leader?.userId;
  } else if (teamTemplate) {
    membersUserIds = teamTemplate.members?.map((m) => m.userId) || [];
    leaderUserId = teamTemplate.leader?.userId;
  } else if (teamUserIds) {
    membersUserIds = teamUserIds;
  }

  const teamIndividualsResponse = useSelector(getIndividualsByUserIds(membersUserIds));
  const isLookingUpMembers = useSelector(getIndividualsPendingByUserIds(membersUserIds));

  // A team of one says so, rather than a dash that reads as a member list that failed to load.
  if (membersUserIds.length === 0 && leaderUserId) {
    return <Typography color="text.secondary" variant="body2">Leader only</Typography>;
  }
  // A dash says the team has no members, so it waits until the directory has answered for them.
  if (isLookingUpMembers) return <Skeleton animation="wave" width={160} />;
  if (!teamIndividualsResponse || teamIndividualsResponse.length === 0) {
    return <NoValueDash />;
  }

  const visibleIndividuals = teamIndividualsResponse.slice(0, MAX_VISIBLE_TEAM_MEMBERS);
  const hiddenCount = teamIndividualsResponse.length - visibleIndividuals.length;

  return (
    <MembersContainer>
      {visibleIndividuals.map((individual: Individual) => (
        <TeamColumnLink
          key={individual.userId.value}
          title={individual.profile.fullName}
          onClick={(event) => {
            event.stopPropagation();
            openDialog(individualProfileDialog(individual));
          }}
        >
          {table ? <SearchMatch table={table} text={individual.profile.fullName} /> : individual.profile.fullName}
        </TeamColumnLink>
      ))}
      <OpenRosterButton
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          setIsRosterOpen(true);
        }}
      >
        {hiddenCount > 0 ? `+${hiddenCount} more` : "View team"}
      </OpenRosterButton>
      <TeamRosterDrawer
        open={isRosterOpen}
        onClose={() => setIsRosterOpen(false)}
        title={props.teamName || "Team"}
        leaderUserId={leaderUserId}
        memberUserIds={membersUserIds}
      />
    </MembersContainer>
  );
}
